import { Star, ShoppingCart, Check } from 'lucide-react';
import { useState } from 'react';
import { useCart } from '../contexts/CartContext';
import { cn, formatPrice } from '../lib/utils';

export interface Product {
  id: string;
  name: string;
  brand: string;
  category: string;
  subcategory: string;
  price: number;
  description: string;
  image_url: string;
  in_stock: boolean;
  stock_quantity: number;
  rating: number;
  tags: string[];
}

interface ProductCardProps {
  product: Product;
  highlighted?: boolean;
  onClick?: () => void;
}

export default function ProductCard({ product, highlighted = false, onClick }: ProductCardProps) {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div
      data-product-id={product.id}
      onClick={onClick}
      className={cn(
        'group bg-card border rounded-xl overflow-hidden card-lift cursor-pointer',
        highlighted
          ? 'border-primary ring-2 ring-primary ring-offset-2 ring-offset-background shadow-lg shadow-primary/20 scale-105'
          : 'border-border hover:border-primary/30',
      )}
    >
      {/* Image */}
      <div className="relative aspect-square bg-background overflow-hidden">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x300?text=No+Image';
          }}
        />
        {!product.in_stock && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
            <span className="text-muted-foreground font-medium">Uitverkocht</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
          {product.brand}
        </p>
        <h3 className="font-medium text-foreground line-clamp-2 mb-2 min-h-[2.5rem]">
          {product.name}
        </h3>
        <div className="flex items-center gap-1 mb-3">
          <Star className="w-4 h-4 fill-primary text-primary" />
          <span className="text-sm text-muted-foreground">{product.rating.toFixed(1)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-foreground">
            {formatPrice(product.price)}
          </span>
          <button
            onClick={handleAddToCart}
            disabled={!product.in_stock}
            className={cn(
              'p-2 rounded-lg transition-all',
              added
                ? 'bg-success text-white'
                : 'bg-primary/10 text-primary hover:bg-primary hover:text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {added ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
